<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useExtendedI18n } from '@/i18n'
import { useFetch } from '@/modules/core/composables/useFetch'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import { useEntityStore } from '../../stores/better-entity-store'
import { useFilingStore } from '@/modules/filing/stores/filing-store'
import type { ExistingFile } from '@/modules/shared/components/the-uploader.vue'
import { useAddressStore } from '@/modules/shared/stores/address-store'
import { useSpvForm } from '../../utils/use-spv-form'
import { useCatalogStore } from '@/modules/billing/stores/catalog-store'
import { useSigningFeatureFlags } from '@/modules/signing/composables/use-signing-feature-flags'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'
import { currencyOptions } from '@/modules/shared/utils/money'
import { toFormData } from '@/modules/shared/utils/form'
import { VConfirmDialog, VNotification } from '@/modules/shared/components'
import { useModal } from '@/modules/shared/composables/use-modal'
import { useUnsavedChangesGuard } from '@/modules/shared/composables/use-unsaved-changes-guard'
import { useOnboarding, type OnboardingStep } from '@/modules/shared/composables/use-onboarding'
import OnboardingShell from '@/modules/shared/components/onboarding/OnboardingShell.vue'
import VShimmerLogo from '@/modules/connecting/components/v-shimmer-logo.vue'

import SpvConfigStep from './components/spv-config-step.vue'
import SpvStructureStep from './components/spv-structure-step.vue'
import SpvAdminStep from './components/spv-admin-step.vue'
import SpvInvestmentStep from './components/spv-investment-step.vue'
import SpvBankStep from './components/spv-bank-step.vue'
import SpvTaxStep from './components/spv-tax-step.vue'
import SpvReviewStep from './components/spv-review-step.vue'

import type { AddOn, Subscription } from '../../types'

// ── Route & Stores ──────────────────────────────────────────────

const route = useRoute()
const router = useRouter()
const { slug } = route.params as { slug: string }
const entityId = computed(() => (route.params as any).entity_id as string | undefined)

const isEditMode = computed(() => route.name === 'investing.spv-edit')

const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const entityStore = useEntityStore()
const filingStore = useFilingStore()
const addressStore = useAddressStore()
const notificationStore = useNotificationStore()
const { n } = useExtendedI18n()
const { isSpvAutoSigningEnabled } = useSigningFeatureFlags()
const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

// ── Form ────────────────────────────────────────────────────────

const isRIA = computed(() => workspaceStore.current.options.is_registered_investment_advisor)
const hasBillingPaid = computed(() => {
  if (!entityStore.entityExpanded) return false
  return entityStore.entityExpanded.is_billing_paid
})
const hasStripeSubscription = computed(() => {
  if (!entityStore.entityExpanded) return false
  return !entityStore.entityExpanded.has_billing_invoices && entityStore.entityExpanded.is_venture360_the_administrator
})
const isLegacyBilling = computed(() => {
  if (!entityStore.entityExpanded) return false
  return entityStore.entityExpanded.is_legacy_billing === true
})
const isSpvConfigDisabled = computed(() => hasBillingPaid.value || hasStripeSubscription.value || isLegacyBilling.value)

const catalogStore = useCatalogStore()

const {
  adminOptions,
  managerOptions,
  activityOptions,
  investmentTypeOptions,
  ownershipTypeOptions,
  subscriptionOptions,
  addOnOptions,
  legalStructureOptions,
  sectorOptions,
  visibilityOptions,
  investmentStageOptions,
  investorOptions,
  initializeSpvForm,
  form,
  v$,
  isPaymentOptionsAvailable,
  totalDue,
  isRecommended,
  useFormWatchers,
  setFormWithEntityData,
  isAccessible,
  buildPayload,
  check_bank_account_number,
  resetFormState,
} = useSpvForm(isEditMode.value ? 'edit' : 'create', isSpvConfigDisabled, isRIA)

// ── Plan resolution ─────────────────────────────────────────────
// Resolves the SPV's selected plan from current options, catalog (legacy), or stripe name (unknown).
// Steps use this to display the plan and determine if it's legacy (not in subscriptionOptions).

const selectedPlan = computed<Subscription | null>(() => {
  const services = entityStore.entityExpanded?.selected_services || form.value.selected_services || []
  const addOnNames = catalogStore.addOnOptions.map((a) => a.value)
  const planName = services.find((s: string) => !addOnNames.includes(s))
  if (!planName) return null

  const currentMatch = subscriptionOptions.value.find((s) => s.value === planName)
  if (currentMatch) return currentMatch

  const catalogPlan = catalogStore.findPlanByStripeName(planName)
  if (catalogPlan) {
    return {
      title: catalogPlan.title,
      value: catalogPlan.stripe_name,
      price: catalogPlan.price,
      included: catalogPlan.included || [],
    }
  }

  return { title: planName, value: planName, price: '', included: [] }
})

// ── Subscription selection ──────────────────────────────────────

const selectedSubscription = ref<Subscription | null>(null)

const shouldShowPaymentOptions = computed(() =>
  isSpvConfigDisabled.value ? !isLegacyBilling.value : isPaymentOptionsAvailable.value,
)

const selectSubscription = (subscription: Subscription) => {
  if (!isAccessible(subscription)) return
  const isAlreadySelected = selectedSubscription.value?.value === subscription.value
  if (isAlreadySelected) {
    form.value.selected_services = form.value.selected_services.filter((p: string) => p !== subscription.value)
  }
  if (selectedSubscription.value) {
    form.value.selected_services = form.value.selected_services.filter(
      (p: string) => p !== selectedSubscription.value!.value,
    )
  }
  selectedSubscription.value = subscription
  if (!form.value.selected_services.includes(subscription.value)) {
    form.value.selected_services.push(subscription.value)
  }
}

const selectAddOn = (addOn: AddOn) => {
  const planExists = form.value.selected_services.includes(addOn.value)
  if (planExists) {
    form.value.selected_services = form.value.selected_services.filter((p: string) => p !== addOn.value)
  } else {
    form.value.selected_services.push(addOn.value)
  }
}

const formattedTotalDue = computed(() => n(totalDue.value, 'currency'))

// ── Watchers (from original Form.vue) ───────────────────────────

watch(
  () => form.value.admin_signer_id,
  (newVal, oldVal) => {
    if (newVal === oldVal) return
    if (form.value.admin_ids.includes(oldVal)) {
      form.value.admin_ids = form.value.admin_ids.filter((id: string) => id !== oldVal)
    }
    if (newVal && !form.value.admin_ids.includes(newVal)) {
      form.value.admin_ids.push(newVal)
    }
  },
)

watch(
  isRIA,
  async (isRegisteredInvestmentAdvisor) => {
    if (isRegisteredInvestmentAdvisor) {
      if (!isEditMode.value) {
        // RIA requires V360 admin with V360 formation docs, no post-year activity
        form.value.is_venture360_the_administrator = true
        await nextTick()
        form.value.is_venture360_providing_formation_documents = true
        await nextTick()
        form.value.expect_any_activity = false
      }
      // Remove add-ons — they're bundled in RIA plan prices
      await nextTick()
      form.value.selected_services = form.value.selected_services.filter(
        (s: string) => !['Blue Sky Filings', 'Form D'].includes(s),
      )
    }
  },
  { immediate: true },
)

watch(
  () => subscriptionOptions.value.find((s) => isRecommended(s)),
  (recommendedSubscription) => {
    if (isEditMode.value) {
      // In edit mode, don't auto-select the recommended plan, but clear
      // the current selection if it's no longer available
      if (selectedSubscription.value && !isAccessible(selectedSubscription.value)) {
        form.value.selected_services = form.value.selected_services.filter(
          (p: string) => p !== selectedSubscription.value!.value,
        )
        selectedSubscription.value = null
      }
      return
    }
    if (recommendedSubscription) {
      selectSubscription(recommendedSubscription)
    } else {
      if (selectedSubscription.value) {
        form.value.selected_services = form.value.selected_services.filter(
          (p: string) => p !== selectedSubscription.value!.value,
        )
      }
      selectedSubscription.value = null
    }
  },
)

// ── Wizard Steps ────────────────────────────────────────────────

const ALL_STEPS: OnboardingStep[] = [
  {
    key: 'config',
    icon: 'Settings',
    title: 'SPV configuration',
    subtitle: 'Configure your SPV and select the right plan.',
  },
  {
    key: 'structure',
    icon: 'Building',
    title: 'General structure',
    subtitle: 'Define the legal and financial structure of your SPV.',
  },
  {
    key: 'admin',
    icon: 'Users',
    title: 'Administration',
    subtitle: 'Assign roles and provide the entity\u2019s address.',
  },
  {
    key: 'investment',
    icon: 'TrendingUp',
    title: 'Investment details',
    subtitle: 'Tell us about the company you are funding.',
  },
  {
    key: 'bank',
    icon: 'Bank',
    title: 'Bank details',
    subtitle: 'Bank information will automatically be populated in capital calls.',
  },
  {
    key: 'tax',
    icon: 'FileLines',
    title: 'Tax details',
    subtitle: 'Configure the tax filing details for this entity.',
  },
  {
    key: 'review',
    icon: 'CheckCircle',
    title: 'Review and confirm',
    subtitle: 'Review your SPV details before finalizing.',
  },
]

const STEPS = ALL_STEPS.filter((s) => s.key !== 'tax' || authStore.is_site_admin)

const currentStepKey = computed(() => STEPS[onboarding.currentStep.value]?.key)

// ── Step refs ───────────────────────────────────────────────────

const configStep = ref<InstanceType<typeof SpvConfigStep> | null>(null)
const structureStep = ref<InstanceType<typeof SpvStructureStep> | null>(null)
const adminStep = ref<InstanceType<typeof SpvAdminStep> | null>(null)
const investmentStep = ref<InstanceType<typeof SpvInvestmentStep> | null>(null)
const bankStep = ref<InstanceType<typeof SpvBankStep> | null>(null)
const taxStep = ref<InstanceType<typeof SpvTaxStep> | null>(null)

const validateStep = async (stepIndex: number): Promise<boolean> => {
  const key = STEPS[stepIndex]?.key
  switch (key) {
    case 'config':
      return (await configStep.value?.isValid()) ?? true
    case 'structure':
      return (await structureStep.value?.isValid()) ?? true
    case 'admin':
      return (await adminStep.value?.isValid()) ?? true
    case 'investment':
      return (await investmentStep.value?.isValid()) ?? true
    case 'bank':
      return (await bankStep.value?.isValid()) ?? true
    case 'tax':
      return (await taxStep.value?.isValid()) ?? true
    default:
      return true
  }
}

const onboarding = useOnboarding({
  steps: STEPS,
  startAtReview: isEditMode.value,
  scrollSelector: '.onboarding-scroll',
  validateStep,
})

// ── Submit ──────────────────────────────────────────────────────

const supportingDocuments = ref<File[]>([])
const existingFiles = ref<ExistingFile[]>([])

const removeExistingFile = async (fileId: string) => {
  await filingStore.removeFile(fileId)
  existingFiles.value = existingFiles.value.filter((f) => f.id !== fileId)
}

const submitError = ref<string | null>(null)
const submitting = ref(false)

const afterCreate = (newEntityId: string, hasSubscription: boolean, isManageByV360: boolean) => {
  if (!newEntityId) return

  if (isSpvAutoSigningEnabled.value && isManageByV360) {
    router.replace({
      name: 'investing.spv.onboarding-document',
      params: { entity_id: newEntityId },
      query: { hasSubscription: hasSubscription ? 'true' : 'false' },
    })
    return
  }

  if (hasSubscription) {
    router.replace({ name: 'billing-entity', params: { billable_id: newEntityId, billable_type: 'spv' } })
    return
  }

  router.replace({ name: 'investing.entity-overview', params: { entity_id: newEntityId, entity_type: 'spv' } })
}

const onSubmit = async () => {
  // Validate all steps before submit
  const valid = await v$.value.$validate()
  if (!valid) {
    // The validation summary renders at the top of Review, but the user clicks
    // Done from the bottom — scroll back up so they actually see why it didn't
    // save. nextTick lets the summary render first; instant scroll (not smooth)
    // avoids the layout-shift cancellation noted in use-onboarding.
    await nextTick()
    const scrollEl = document.querySelector('.onboarding-scroll')
    if (scrollEl) scrollEl.scrollTop = 0
    return
  }

  submitError.value = null
  submitting.value = true

  try {
    const payload = buildPayload()
    if (isEditMode.value && entityId.value) {
      payload.id = entityId.value
    }

    if (supportingDocuments.value.length > 0 && payload.deal_card_attributes) {
      payload.deal_card_attributes.supporting_documents = supportingDocuments.value
    }

    const hasFiles = supportingDocuments.value.length > 0
    const body = hasFiles ? toFormData({ spv: payload }) : { spv: payload }
    const data: any = await useFetch(`/v4/${slug}/investing/investor-sets/setup-spv`).post(body)

    leaveGuard.allowLeave()

    if (isEditMode.value) {
      router.replace({ name: 'investing.entity-overview', params: { entity_id: entityId.value, entity_type: 'spv' } })
    } else {
      afterCreate(data?.id, payload.selected_services.length > 0, payload.is_venture360_the_administrator)
    }
  } catch (error: any) {
    submitError.value = error?.message || 'An unexpected error occurred. Please try again.'
    notificationStore.enqueue('error', submitError.value!)
  } finally {
    submitting.value = false
  }
}

// ── Delete (edit mode only) ─────────────────────────────────────

const deleteModal = useModal()
const deleting = ref(false)

const handleConfirmDelete = async () => {
  if (deleting.value || !entityId.value) return
  deleting.value = true
  try {
    await entityStore.deleteEntity(entityId.value, { slug })
    deleteModal.close()
    router.replace({ name: 'investing.entities', params: { slug, entity_type: 'spvs' } })
  } catch (error: any) {
    notificationStore.enqueue('error', error?.message || 'Failed to delete SPV. Please try again.')
  } finally {
    deleting.value = false
  }
}

// ── Navigation guard (create flow only) ─────────────────────────

const leaveGuard = useUnsavedChangesGuard({ enabled: () => !isEditMode.value })

// ── Mount ───────────────────────────────────────────────────────

onMounted(async () => {
  showSkeleton()
  resetFormState()
  entityStore.reset()

  await workspaceStore.fetchCurrent()
  await Promise.all([initializeSpvForm(), addressStore.fetchCountries(), addressStore.fetchStates('US')])

  if (isEditMode.value && entityId.value) {
    await entityStore.fetchEntity(entityId.value, { slug })
    setFormWithEntityData(entityStore.entityExpanded)

    // Fetch existing files from deal card folder
    const dealCardFolderId = entityStore.entityExpanded?.deal_card?.filing_folder_id
    if (dealCardFolderId) {
      await filingStore.fetchFiles(dealCardFolderId)
      existingFiles.value = filingStore.files.filter((f) => f.filing_folder_id === dealCardFolderId)
    }

    // Restore selected subscription from entity data
    const selectedSub = subscriptionOptions.value.find((s) =>
      form.value.selected_services.some((service: string) => s.value === service),
    )
    if (selectedSub) selectSubscription(selectedSub)
  }

  if (!isEditMode.value) useFormWatchers()

  watch(
    () => entityStore.entityExpanded,
    (newEntity) => {
      if (!newEntity || !isEditMode.value) return
      setFormWithEntityData(newEntity)
      useFormWatchers()

      const selectedSub = subscriptionOptions.value.find((s) =>
        form.value.selected_services.some((service: string) => s.value === service),
      )
      if (selectedSub) selectSubscription(selectedSub)
    },
  )

  hideSkeleton()
})
</script>

<template>
  <OnboardingShell
    :steps="STEPS"
    :current-step="onboarding.currentStep.value"
    :highest-step-reached="onboarding.highestStepReached.value"
    :is-first-step="onboarding.isFirstStep.value"
    :is-last-step="onboarding.isLastStep.value"
    :step-menu-open="onboarding.stepMenuOpen.value"
    :loading="skeleton"
    :submitting="submitting"
    :submit-label="isEditMode ? 'Done' : 'Create SPV'"
    @update:step-menu-open="onboarding.stepMenuOpen.value = $event"
    @continue="onboarding.onClickContinue"
    @back="onboarding.onClickBack"
    @submit="onSubmit"
    @step-click="onboarding.onStepClick"
  >
    <template #notification>
      <VNotification />
    </template>

    <template #loading>
      <VShimmerLogo />
    </template>

    <SpvConfigStep
      v-if="currentStepKey === 'config'"
      ref="configStep"
      :form="form"
      :v$="v$"
      :subscription-options="subscriptionOptions"
      :add-on-options="addOnOptions"
      :activity-options="activityOptions"
      :is-spv-config-disabled="isSpvConfigDisabled"
      :is-edit-mode="isEditMode"
      :isRIA="isRIA"
      :is-payment-options-available="isPaymentOptionsAvailable"
      :selected-subscription="selectedSubscription"
      :should-show-payment-options="shouldShowPaymentOptions"
      :selected-plan="selectedPlan"
      :total-due="formattedTotalDue"
      :initial-value="entityStore.entityExpanded"
      :is-accessible="isAccessible"
      :is-recommended="isRecommended"
      @select-subscription="selectSubscription"
      @select-add-on="selectAddOn"
    />

    <SpvStructureStep
      v-if="currentStepKey === 'structure'"
      ref="structureStep"
      :form="form"
      :v$="v$"
      :legal-structure-options="legalStructureOptions"
      :ownership-type-options="ownershipTypeOptions"
      :currency-options="currencyOptions"
    />

    <SpvAdminStep
      v-if="currentStepKey === 'admin'"
      ref="adminStep"
      :form="form"
      :v$="v$"
      :manager-options="managerOptions"
      :admin-options="adminOptions"
    />

    <SpvInvestmentStep
      v-if="currentStepKey === 'investment'"
      ref="investmentStep"
      :form="form"
      :v$="v$"
      :investment-type-options="investmentTypeOptions"
      :sector-options="sectorOptions"
      :visibility-options="visibilityOptions"
      :investment-stage-options="investmentStageOptions"
      :investor-options="investorOptions"
      :files="supportingDocuments"
      :existing-files="existingFiles"
      @update:files="supportingDocuments = $event"
      @remove:existing-file="removeExistingFile"
    />

    <SpvBankStep
      v-if="currentStepKey === 'bank'"
      ref="bankStep"
      :form="form"
      :v$="v$"
      :is-new-record="!isEditMode"
      :check-bank-account-number="check_bank_account_number"
    />

    <SpvTaxStep v-if="currentStepKey === 'tax'" ref="taxStep" :form="form" :v$="v$" />

    <SpvReviewStep
      v-if="currentStepKey === 'review'"
      :form="form"
      :v$="v$"
      :steps="STEPS"
      :subscription-options="subscriptionOptions"
      :add-on-options="addOnOptions"
      :selected-plan="selectedPlan"
      :ownership-type-options="ownershipTypeOptions"
      :manager-options="managerOptions"
      :admin-options="adminOptions"
      :isRIA="isRIA"
      :is-recommended="isRecommended"
      :total-due="formattedTotalDue"
      :files="supportingDocuments"
      :existing-files="existingFiles"
      :is-edit-mode="isEditMode"
      @navigate="onboarding.navigateFromReview"
      @delete="deleteModal.open()"
    />
  </OnboardingShell>

  <VConfirmDialog
    :modalStore="deleteModal"
    title="Delete SPV"
    message="Deleting this SPV will permanently remove all associated data including commitments, capital calls, distributions, and documents. This action cannot be undone."
    confirmLabel="Delete SPV"
    variant="danger"
    :loading="deleting"
    @confirm="handleConfirmDelete"
  />

  <VConfirmDialog
    :modalStore="leaveGuard.modal"
    title="Discard SPV setup?"
    message="If you leave this page, all the information you've entered will be lost. Are you sure you want to discard your progress?"
    confirmLabel="Discard"
    variant="danger"
    @confirm="leaveGuard.confirm"
    @cancel="leaveGuard.cancel"
  />
</template>
